import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { User, Shield, Activity, LogOut, ExternalLink, Plus, FileText, Check, Zap, History } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCENT = '#D4AF37';
const TEXT = '#F5F5F5';
const SUBTEXT = '#A3A3A3';
const SURFACE = '#0A0A0A';
const BORDER = '#1f2937';

export default function Dashboard() {
  const { t } = useLanguage();
  const { user, loading, refreshUser, token } = useAuth();
  const navigate = useNavigate();
  const [reportPacks, setReportPacks] = useState([]);
  const [recharges, setRecharges] = useState({ items: [], total_purchased: 0, total_used: 0, balance: 0 });
  const [autoReorderSaving, setAutoReorderSaving] = useState(false);

  const fetchRecharges = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users/me/recharges`, { headers: { Authorization: `Bearer ${token}` } });
      setRecharges(res.data);
    } catch (e) { /* ignore */ }
  }, [token]);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/pricing`)
      .then(res => {
        if (!mounted) return;
        const packs = (res.data || []).filter(p => p.category === 'reports');
        setReportPacks(packs.sort((a, b) => a.sort_order - b.sort_order));
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (token) fetchRecharges();
  }, [token, fetchRecharges]);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/');
    }
  }, [loading, user, navigate]);

  const toggleAutoReorder = async (enabled) => {
    setAutoReorderSaving(true);
    try {
      const pack_id = user?.auto_reorder_pack_id || (reportPacks.find(p => p.popular) || reportPacks[0])?.id;
      await axios.put(
        `${API}/users/me/auto-reorder`,
        {
          auto_reorder_enabled: enabled,
          auto_reorder_pack_id: enabled ? pack_id : null,
          auto_reorder_threshold: user?.auto_reorder_threshold || 5,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshUser();
      toast.success(enabled ? 'Ricarica automatica attivata' : 'Ricarica automatica disattivata');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore aggiornamento auto-reorder');
    } finally {
      setAutoReorderSaving(false);
    }
  };

  const setAutoReorderPack = async (pack_id) => {
    setAutoReorderSaving(true);
    try {
      await axios.put(
        `${API}/users/me/auto-reorder`,
        {
          auto_reorder_enabled: true,
          auto_reorder_pack_id: pack_id,
          auto_reorder_threshold: user?.auto_reorder_threshold || 5,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshUser();
      toast.success('Pacchetto auto-reorder aggiornato');
    } catch (e) {
      toast.error('Errore aggiornamento');
    } finally {
      setAutoReorderSaving(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-[#A3A3A3] text-sm">Caricamento...</div>
      </div>
    );
  }

  const hasActiveSubscription =
    user.is_active && user.plan && user.plan !== 'trial' && !user.plan.startsWith('reports_');
  const balance = user.reports_balance || 0;
  const threshold = user.auto_reorder_threshold || 5;
  const lowBalance = hasActiveSubscription && balance < threshold;
  const autoEnabled = !!user.auto_reorder_enabled;

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      <Navbar onAuthClick={() => {}} />

      <main className="flex-1 pt-28 pb-16 px-6 md:px-12 lg:px-24" data-testid="dashboard-page">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-10">
            <h1 className="font-heading font-bold text-3xl lg:text-4xl text-[#F5F5F5]">
              Dashboard
            </h1>
            <p className="text-[#A3A3A3] mt-2">
              Benvenuto, <span style={{ color: ACCENT }}>{user.name}</span>
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-8">
            <StatCard label="Account Status" icon={User}
              value={user.is_active ? 'Attivo' : 'In Attesa'}
              valueColor={user.is_active ? '#22c55e' : '#facc15'} />
            <StatCard label="Piano Attuale" icon={Shield}
              value={user.plan ? user.plan.replace(/_/g, ' ') : 'Nessun piano'}
              valueColor={ACCENT} capitalize />
            <StatCard label="OSINT Account" icon={Activity}
              value={user.osint_account_activated ? 'Attivato' : 'Non attivato'}
              valueColor={user.osint_account_activated ? '#22c55e' : SUBTEXT} />
            <StatCard label="Report Saldo" icon={FileText}
              value={balance.toString()}
              valueColor={lowBalance ? '#facc15' : ACCENT} />
          </div>

          {/* ============ Ricarica Report widget ============ */}
          {reportPacks.length > 0 && (
            <div
              className="mb-10 border rounded-sm overflow-hidden"
              style={{ borderColor: 'rgba(212,175,55,0.30)', background: 'linear-gradient(180deg, rgba(212,175,55,0.05) 0%, transparent 100%)' }}
              data-testid="dashboard-reports-widget"
            >
              {/* Widget header */}
              <div className="px-6 lg:px-8 py-5 border-b" style={{ borderColor: BORDER }}>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-sm flex items-center justify-center" style={{ background: ACCENT }}>
                      <Plus size={20} color="#050505" strokeWidth={3} />
                    </div>
                    <div>
                      <h2 className="font-semibold text-lg text-[#F5F5F5]">
                        Report Business Information
                      </h2>
                      <p className="text-xs text-[#A3A3A3] mt-1">
                        Rating · Fido · Eventi negativi · Dati societari · Bilanci · Persone &amp; aziende
                      </p>
                    </div>
                  </div>

                  {/* Balance big number */}
                  {hasActiveSubscription && (
                    <div className="text-right">
                      <div className="font-mono font-bold text-3xl" style={{ color: lowBalance ? '#facc15' : ACCENT }}>
                        {balance}
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color: SUBTEXT }}>
                        Rimangono {balance === 1 ? '1 report' : `${balance} report`}
                      </div>
                    </div>
                  )}
                </div>
                {lowBalance && (
                  <div
                    className="mt-4 px-3 py-2 rounded-sm flex items-center gap-2"
                    style={{ background: 'rgba(250,204,21,0.1)', border: '1px solid rgba(250,204,21,0.3)' }}
                  >
                    <Zap size={14} color="#facc15" />
                    <span className="text-xs text-[#facc15]">
                      Saldo sotto soglia di {threshold} report — considera di ricaricare
                    </span>
                  </div>
                )}
              </div>

              <div className="p-6 lg:p-8">
                {!hasActiveSubscription ? (
                  <div className="text-center py-4">
                    <p className="text-[#A3A3A3] text-sm mb-4 max-w-md mx-auto">
                      I pacchetti Report sono <strong className="text-[#F5F5F5]">ricariche per abbonamenti attivi</strong>.
                      Attiva prima un piano Starter o Professional per acquistarli.
                    </p>
                    <Button
                      onClick={() => navigate('/subscribe')}
                      className="bond-btn bond-btn-primary"
                      data-testid="activate-subscription-cta"
                    >
                      Attiva un abbonamento
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* Auto-reorder toggle */}
                    <div
                      className="mb-6 p-4 rounded-sm flex items-start justify-between gap-4"
                      style={{ background: '#050505', border: `1px solid ${BORDER}` }}
                      data-testid="auto-reorder-section"
                    >
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Zap size={14} style={{ color: ACCENT }} />
                          <p className="text-sm font-semibold text-[#F5F5F5]">Ricarica automatica</p>
                        </div>
                        <p className="text-[11px] text-[#A3A3A3]">
                          {autoEnabled
                            ? `Attiva — quando scendi sotto ${threshold} report ricevi notifica per ricarica ${user.auto_reorder_pack_id?.replace('reports_pack_', '') || ''} report`
                            : 'Disattivata — ricarica manuale quando vuoi'}
                        </p>
                      </div>
                      <Switch
                        checked={autoEnabled}
                        onCheckedChange={toggleAutoReorder}
                        disabled={autoReorderSaving}
                        data-testid="auto-reorder-toggle"
                      />
                    </div>

                    {/* Report pack cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      {reportPacks.map((pack) => {
                        const isAutoReorderPack = autoEnabled && user.auto_reorder_pack_id === pack.id;
                        return (
                          <div
                            key={pack.id}
                            className={`relative rounded-sm border p-6 flex flex-col transition-all hover:-translate-y-0.5 ${
                              pack.popular
                                ? 'border-[#D4AF37]/70 shadow-[0_0_30px_rgba(212,175,55,0.18)]'
                                : 'border-[#1f2937] hover:border-[#D4AF37]/40'
                            }`}
                            style={{ background: SURFACE }}
                            data-testid={`reports-pack-${pack.id}`}
                          >
                            {pack.popular && (
                              <span
                                className="absolute -top-2.5 right-4 text-[9px] font-bold uppercase tracking-[0.25em] px-2.5 py-1 rounded-sm"
                                style={{ background: ACCENT, color: '#050505' }}
                              >
                                Migliore
                              </span>
                            )}

                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-2.5">
                                <FileText size={18} style={{ color: ACCENT }} />
                                <h3 className="font-semibold text-base text-[#F5F5F5]">{pack.name_it}</h3>
                              </div>
                              {isAutoReorderPack && (
                                <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-sm" style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e' }}>
                                  Auto
                                </span>
                              )}
                            </div>

                            <div className="mb-4">
                              <div className="flex items-baseline gap-2">
                                <span className="font-heading font-bold text-3xl text-[#F5F5F5]">€{pack.price}</span>
                                {pack.original_price && (
                                  <span className="line-through text-sm text-[#A3A3A3] opacity-70">€{pack.original_price}</span>
                                )}
                              </div>
                              <p className="text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3] mt-1">una tantum</p>
                            </div>

                            <ul className="text-xs text-[#E5E5E5] space-y-1.5 mb-5 flex-1">
                              {(pack.features_it || []).slice(0, 4).map((f, i) => (
                                <li key={i} className="flex items-start gap-2">
                                  <Check size={12} className="mt-0.5 flex-shrink-0" style={{ color: ACCENT }} />
                                  <span>{f}</span>
                                </li>
                              ))}
                            </ul>

                            <div className="flex flex-col gap-2">
                              <Button
                                onClick={() => navigate(`/subscribe?plan=${pack.id}`)}
                                className={`bond-btn ${pack.popular ? 'bond-btn-primary' : 'bond-btn-secondary'} w-full`}
                                data-testid={`buy-${pack.id}`}
                              >
                                Acquista ricarica
                              </Button>
                              {autoEnabled && !isAutoReorderPack && (
                                <button
                                  onClick={() => setAutoReorderPack(pack.id)}
                                  disabled={autoReorderSaving}
                                  className="text-[11px] text-[#A3A3A3] hover:text-[#D4AF37] underline-offset-2 hover:underline transition-colors"
                                  data-testid={`set-auto-${pack.id}`}
                                >
                                  Usa per auto-reorder
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ============ Cronologia ricariche ============ */}
          {recharges.items.length > 0 && (
            <div
              className="mb-10 rounded-sm border"
              style={{ borderColor: BORDER, background: SURFACE }}
              data-testid="recharges-history"
            >
              <div className="px-6 lg:px-8 py-5 border-b flex items-center justify-between" style={{ borderColor: BORDER }}>
                <div className="flex items-center gap-3">
                  <History size={18} style={{ color: ACCENT }} />
                  <h2 className="font-semibold text-base text-[#F5F5F5]">Cronologia ricariche</h2>
                </div>
                <div className="text-right text-xs">
                  <div className="text-[#A3A3A3]">
                    <span>Acquistati: </span>
                    <strong className="text-[#F5F5F5]">{recharges.total_purchased}</strong>
                    <span className="mx-2">·</span>
                    <span>Usati: </span>
                    <strong className="text-[#F5F5F5]">{recharges.total_used}</strong>
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">
                      <th className="text-left px-6 py-3">Data</th>
                      <th className="text-left px-6 py-3">Pacchetto</th>
                      <th className="text-right px-6 py-3">Report</th>
                      <th className="text-right px-6 py-3">Importo</th>
                      <th className="text-left px-6 py-3">Metodo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recharges.items.map((r) => (
                      <tr key={r.id} className="border-t" style={{ borderColor: BORDER }}>
                        <td className="px-6 py-3 text-[#E5E5E5] font-mono text-xs">
                          {new Date(r.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </td>
                        <td className="px-6 py-3 text-[#F5F5F5]">{r.plan_name}</td>
                        <td className="px-6 py-3 text-right font-mono" style={{ color: ACCENT }}>+{r.reports_qty}</td>
                        <td className="px-6 py-3 text-right font-mono text-[#F5F5F5]">€{r.amount}</td>
                        <td className="px-6 py-3 text-[#A3A3A3] uppercase text-xs">{r.payment_method}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Main content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Account info */}
            <div className="lg:col-span-2">
              <div className="bg-[#0A0A0A] border border-[#1f2937] rounded-sm p-8">
                <h2 className="font-heading font-semibold text-xl text-[#F5F5F5] mb-6">
                  Informazioni Account
                </h2>

                <div className="space-y-1">
                  <div className="flex justify-between py-3 border-b border-[#1f2937]">
                    <span className="text-[#A3A3A3]">Nome</span>
                    <span className="text-[#F5F5F5]">{user.name}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-[#1f2937]">
                    <span className="text-[#A3A3A3]">Email</span>
                    <span className="text-[#F5F5F5] font-mono text-sm">{user.email}</span>
                  </div>
                  {user.eddi_pseudonym && (
                    <div className="flex justify-between py-3 border-b border-[#1f2937] gap-3">
                      <span className="text-[#A3A3A3] flex-shrink-0">Identificativo BOND</span>
                      <span
                        className="text-[#A3A3A3] font-mono text-xs truncate"
                        title="Identificativo opaco con cui la piattaforma riconosce il tuo account. La tua email reale resta privata."
                      >
                        {user.eddi_pseudonym}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between py-3">
                    <span className="text-[#A3A3A3]">Data Registrazione</span>
                    <span className="text-[#F5F5F5]">
                      {new Date(user.created_at).toLocaleDateString('it-IT')}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick actions */}
            <div className="lg:col-span-1">
              <div className="bg-[#0A0A0A] border border-[#1f2937] rounded-sm p-8">
                <h2 className="font-heading font-semibold text-xl text-[#F5F5F5] mb-6">
                  Azioni Rapide
                </h2>

                <div className="space-y-3">
                  {user.is_active && (
                    <a
                      href="https://smartinfo.test.osintlab.it/auth/login"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <Button className="bond-btn bond-btn-primary w-full">
                        <ExternalLink size={16} />
                        Accedi a BOND
                      </Button>
                    </a>
                  )}

                  {!user.plan && (
                    <Button
                      onClick={() => navigate('/subscribe')}
                      className="bond-btn bond-btn-primary w-full"
                    >
                      Scegli un Piano
                    </Button>
                  )}

                  <Button
                    onClick={() => window.open('https://www.smart-info.it/contatti', '_blank')}
                    className="bond-btn bond-btn-secondary w-full"
                  >
                    Contatti e Assistenza
                  </Button>

                  <Button
                    onClick={() => navigate('/')}
                    variant="ghost"
                    className="w-full text-[#A3A3A3] hover:text-[#D4AF37] flex items-center justify-center gap-2"
                  >
                    <LogOut size={16} />
                    Torna alla home
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}


function StatCard({ label, icon: Ico, value, valueColor, capitalize }) {
  return (
    <div className="bg-[#0A0A0A] border border-[#1f2937] rounded-sm p-5">
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-sm flex items-center justify-center"
          style={{ background: 'rgba(212,175,55,0.1)' }}
        >
          <Ico size={18} style={{ color: '#D4AF37' }} />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-1">{label}</p>
          <p className={`font-semibold text-base truncate ${capitalize ? 'capitalize' : ''}`} style={{ color: valueColor }}>
            {value}
          </p>
        </div>
      </div>
    </div>
  );
}
